import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { createConsumer } from "@rails/actioncable"
import { chatAttachmentSupport } from "./concerns/chat_attachment_support"

export default class extends chatAttachmentSupport(Controller) {
  static promoCookieKey = "chat_widget_promo_dismissed_v1"
  static targets = ["toggleButton", "chatPopup", "promoPopup", "contentArea", "chatInput", "fileInput", "attachmentPreview", "attachmentData", "uploadProgress", "sendButton", "metadataUrl", "metadataScreenWidth", "metadataScreenHeight", "metadataTimezone", "metadataLanguage", "supportDot", "supportStatus", "dropZone", "rateLimitNotice"]
  static values = { isLoaded: Boolean, userId: Number, guestToken: String, rateLimitUrl: String }

  connect() {
    this.maybeShowPromo()

    // Initialize rate limiting state
    this.isRateLimited = false
    this.rateLimitRetryAfter = 0
    this.checkRateLimit()

    if (this.isLoadedValue) {
      this.startPresence()
      return
    }

    get("/chats", {
      responseKind: "turbo-stream"
    }).then(() => {
      this.isLoadedValue = true
      this.subscribe()
    })

    document.addEventListener("turbo:submit-start", () => {
      this.chatInputTarget.disabled = true
    })

    document.addEventListener("turbo:submit-end", (event) => {
      const { success, fetchResponse } = event.detail

      // Handle rate limit error (429)
      if (!success && fetchResponse?.status === 429) {
        // Rate limit response will be handled by Turbo Stream
        this.chatInputTarget.disabled = true
        return
      }

      // Check rate limit status after successful submit
      if (success) {
        this.checkRateLimit()
      }

      this.chatInputTarget.disabled = false
      this.chatInputTarget.value = ""
      this.clearAttachment()
      this.chatInputTarget.focus()
    })

    this.startPresence()
  }

  triggerFileInput() {
    this.fileInputTarget.click()
  }

  beforeSubmit(event) {
    // Check rate limit before submitting
    if (this.isRateLimited) {
      event.preventDefault()
      this.showRateLimitNotice(this.rateLimitRetryAfter || 60)
      return
    }

    if (this.hasMetadataUrlTarget) {
      this.metadataUrlTarget.value = window.location.href
    }
    if (this.hasMetadataScreenWidthTarget) {
      this.metadataScreenWidthTarget.value = window.screen.width
    }
    if (this.hasMetadataScreenHeightTarget) {
      this.metadataScreenHeightTarget.value = window.screen.height
    }
    if (this.hasMetadataTimezoneTarget) {
      this.metadataTimezoneTarget.value = Intl.DateTimeFormat().resolvedOptions().timeZone
    }
    if (this.hasMetadataLanguageTarget) {
      this.metadataLanguageTarget.value = navigator.language
    }
  }

  toggle() {
    const willOpen = this.chatPopupTarget.classList.contains("hidden")
    this.chatPopupTarget.classList.toggle("hidden");
    this.toggleButtonTarget.classList.toggle("hidden");
    this.chatInputTarget.focus()

    if (willOpen) {
      this.hidePromo()
    }

    if (willOpen) {
      this.recordActivity()
      get("/chats?mark_read=1", { responseKind: "turbo-stream" })
    }
  }

  openFromPromo() {
    this.hidePromo()
    if (this.chatPopupTarget.classList.contains("hidden")) {
      this.toggle()
    }
  }

  dismissPromo() {
    this.hidePromo()
    this.writePromoCookie()
  }

  recordActivity() {
    const now = Date.now()
    if (this.lastPresenceActivityAt && (now - this.lastPresenceActivityAt) < 30000) return
    this.lastPresenceActivityAt = now
    this.sendPresence("activity")
  }

  scrollToBottom(force = false, smooth = false) {
    if (force || this.atBottom()) {
      this.contentAreaTarget.scrollTo({
        top: this.contentAreaTarget.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto'
      })
    }
  }

  atBottom() {
    // don’t yank the user if they’re reading history
    const threshold = 64
    return (this.contentAreaTarget.scrollHeight - this.contentAreaTarget.scrollTop - this.contentAreaTarget.clientHeight) < threshold
  }

  disconnect() {
    this.unsubscribe()
    this.stopPresence()

    // Clear rate limit timer
    if (this.rateLimitTimer) {
      clearTimeout(this.rateLimitTimer)
      this.rateLimitTimer = null
    }
  }

  startPresence() {
    if (this.presenceStarted) return
    if (!this.userIdValue && !this.hasGuestTokenValue) return

    if (!this.consumer) {
      this.consumer = createConsumer()
    }

    this.presenceStarted = true
    const role = this.userIdValue ? "user" : "guest_user"

    this.presenceSubscription = this.consumer.subscriptions.create({ channel: "ChatPresenceChannel", role: role }, {
      received: (data) => {
        this.handlePresenceMessage(data)
      }
    })

    this.sendPresence("activity")
    this.sendPresence("heartbeat")
    this.presenceHeartbeat = setInterval(() => this.sendPresence("heartbeat"), 60000)
  }

  stopPresence() {
    if (this.presenceHeartbeat) {
      clearInterval(this.presenceHeartbeat)
      this.presenceHeartbeat = null
    }

    if (this.presenceSubscription) {
      try { this.presenceSubscription.unsubscribe() } catch (_) {}
      this.presenceSubscription = null
    }

    this.presenceStarted = false
  }

  sendPresence(action) {
    if (!this.presenceSubscription) return

    const payload = this.presencePayload()
    if (!payload) return

    this.presenceSubscription.perform(action, payload)
  }

  presencePayload() {
    if (this.userIdValue) {
      return { actor_type: "user", actor_id: this.userIdValue }
    }

    if (this.hasGuestTokenValue) {
      return { actor_type: "guest_user", actor_token: this.guestTokenValue }
    }

    return null
  }

  handlePresenceMessage(data) {
    if (!data || data.scope !== "support") return

    const label = this.presenceLabel(data.state, data.away_for_seconds)
    const dotClass = this.presenceDotClass(data.state)

    if (this.hasSupportStatusTarget) {
      this.supportStatusTarget.textContent = label
    }

    if (this.hasSupportDotTarget) {
      this.supportDotTarget.classList.remove("bg-emerald-400", "bg-amber-400", "bg-slate-500")
      this.supportDotTarget.classList.add(dotClass)
      this.supportDotTarget.setAttribute("title", label)
      this.supportDotTarget.setAttribute("aria-label", label)
    }
  }

  presenceLabel(state, awayForSeconds) {
    if (state === "online") return "Online"
    if (state === "offline") return "We'll be back soon"

    const seconds = Number(awayForSeconds || 0)
    if (seconds <= 0) return "Away"

    const minutes = Math.max(1, Math.floor(seconds / 60))
    return `Away ${minutes}m`
  }

  presenceDotClass(state) {
    if (state === "online") return "bg-emerald-400"
    if (state === "away") return "bg-amber-400"
    return "bg-slate-500"
  }

  subscribe() {
    // Prefer user subscription; fall back to guest when available
    if (!this.userIdValue && !this.hasGuestTokenValue) return

    if (!this.consumer) {
      this.consumer = createConsumer()
    }

    const identifier = { channel: "ChatChannel" }
    if (this.userIdValue) {
      identifier.user_id = this.userIdValue
    } else if (this.hasGuestTokenValue) {
      identifier.guest_token = this.guestTokenValue
    }

    this.unsubscribe()
    this.subscription = this.consumer.subscriptions.create(identifier, {
      received: (data) => {
        // Only refresh if event is for this user/guest
        const isForUser = this.userIdValue && data && data.partner_type === 'user' && Number(data.partner_id) === this.userIdValue
        const isForGuest = this.hasGuestTokenValue && data && data.partner_type === 'guest_user'
        if (!(isForUser || isForGuest)) return

        // Handle rate limit reset from admin reply
        if (data.rate_limit_reset) {
          this.clearRateLimit()
        }

        // Re-render messages via Turbo Stream to keep rendering consistent with server
        const shouldMarkRead = !this.chatPopupTarget.classList.contains("hidden")
        const url = shouldMarkRead ? "/chats?mark_read=1" : "/chats"

        get(url, { responseKind: "turbo-stream" }).then(() => {
          // Smooth scroll only if user was at bottom
          setTimeout(() => this.scrollToBottom(true, true), 100)
        })
      }
    })
  }

  unsubscribe() {
    if (this.subscription) {
      try { this.subscription.unsubscribe() } catch (_) {}
      this.subscription = null
    }
  }

  maybeShowPromo() {
    if (!this.hasPromoPopupTarget) return
    if (this.readPromoCookie()) return
    this.promoPopupTarget.classList.remove("hidden")
  }

  hidePromo() {
    if (!this.hasPromoPopupTarget) return
    this.promoPopupTarget.classList.add("hidden")
  }

  readPromoCookie() {
    const key = this.constructor.promoCookieKey
    return document.cookie.split("; ").some((cookie) => cookie.startsWith(`${key}=1`))
  }

  writePromoCookie() {
    const key = this.constructor.promoCookieKey
    document.cookie = `${key}=1; path=/; samesite=lax`
  }

  async checkRateLimit() {
    if (!this.hasRateLimitUrlValue) return

    try {
      const response = await fetch(this.rateLimitUrlValue)
      if (response.ok) {
        const data = await response.json()
        if (data.rate_limited) {
          this.applyRateLimit(data.retry_after)
        } else {
          this.clearRateLimit()
        }
      }
    } catch (error) {
      console.error("Failed to check rate limit:", error)
    }
  }

  applyRateLimit(retryAfter) {
    this.isRateLimited = true
    this.rateLimitRetryAfter = retryAfter
    this.chatInputTarget.disabled = true
    this.showRateLimitNotice(retryAfter)

    // Auto-clear rate limit after the retry period
    if (this.rateLimitTimer) {
      clearTimeout(this.rateLimitTimer)
    }
    this.rateLimitTimer = setTimeout(() => {
      this.clearRateLimit()
    }, retryAfter * 1000)
  }

  clearRateLimit() {
    this.isRateLimited = false
    this.rateLimitRetryAfter = 0
    this.chatInputTarget.disabled = false
    this.hideRateLimitNotice()

    if (this.rateLimitTimer) {
      clearTimeout(this.rateLimitTimer)
      this.rateLimitTimer = null
    }
  }

  showRateLimitNotice(retryAfter) {
    if (!this.hasRateLimitNoticeTarget) return

    this.rateLimitNoticeTarget.innerHTML = `
      <div class="bg-amber-500/20 border-t border-amber-500/30 px-3 py-2">
        <div class="flex items-center gap-2 text-amber-200 text-xs">
          <iconify-icon class="flex-shrink-0" icon="mdi-alert-circle"></iconify-icon>
          <span>Too many messages. Please wait ${retryAfter} seconds before sending another message.</span>
        </div>
      </div>
    `
    this.rateLimitNoticeTarget.classList.remove("hidden")
  }

  hideRateLimitNotice() {
    if (!this.hasRateLimitNoticeTarget) return
    this.rateLimitNoticeTarget.classList.add("hidden")
    this.rateLimitNoticeTarget.innerHTML = ""
  }
}
